import React, {
  useMemo, useState, useCallback, ReactElement,
} from 'react';
import Widget from '../../../../components/widget/widget.component';
import CheckAccess from '../../../../components/check-access/check-access.component';
import StatusBar from '../../../../components/status-bar/status-bar.component';
import CommentForm from '../../../app/common/comment-form/comment-form.component';
import CommentTable from '../../../app/common/comment-table/comment-table.component';
import CallIcon from '../../../../icons/call-icon';
import EmailIcon from '../../../../icons/email-icon';
import MeetingIcon from '../../../../icons/meeting-icon';
import FollowUpIcon from '../../../../icons/follow-up-icon';
import CommentService from '../../../../services/comment-service';
import FormatService from '../../../../services/format-service';
import StructureService from '../../../../services/structure-service';
import {
  CONTACT_COMMENT_CREATE, CONTACT_COMMENT_DELETE, CONTACT_COMMENT_EDIT, CONTACT_COMMENT_LIST_VIEW, CONTACT_VIEW,
  SEARCH_RESULT_VIEW,
} from '../../../../constants/entity/permissions';
import {
  COMMENT_LABEL_CALL, COMMENT_LABEL_EMAIL, COMMENT_LABEL_FOLLOW_UP, COMMENT_LABEL_MEETING,
} from '../../../../constants/entity/comment-labels';
import { ROUTE_CONTACT, ROUTE_SEARCH } from '../../../../constants/system/routes';
import { SIGN_HASH, SIGN_AT } from '../../../../constants/system/signs';
import { HTML_TAG_NAME_ANCHOR } from '../../../../constants/system/html-tag-names';
import { DATA_CONTACT_ID } from '../../../../constants/system/data-attributes';
import {
  KEYBOARD_KEY_ARROW_UP, KEYBOARD_KEY_ENTER, KEYBOARD_KEY_ESCAPE,
} from '../../../../constants/system/keyboard-keys';

const ContactWidgetActivity: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const WIDGET_TITLE_DEFAULT: string = 'Activity';
  const WIDGET_NAME: string = 'widget-contact-activity';

  const LABEL_ICON_MAP: Record<string, ReactElement> = useMemo((): Record<string, ReactElement> => ({
    [COMMENT_LABEL_CALL]: <CallIcon />,
    [COMMENT_LABEL_EMAIL]: <EmailIcon />,
    [COMMENT_LABEL_MEETING]: <MeetingIcon />,
    [COMMENT_LABEL_FOLLOW_UP]: <FollowUpIcon />,
  }), []);

  const {
    isCommentSaving,
    contactData,
    currentUser,
    queryCommentId,
    commentLabelList,
    contactLinkedUserList,
    tagList,
    onAddComment,
    onUpdateComment,
    onDeleteComment,
    onCommentEntityLinkClick,
  } = props;

  const commentList: Comment[] = (contactData && contactData.comments) || [];

  const [commentText, setCommentText]: [string | null, Function] = useState(null);
  const [commentAttachments, setCommentAttachments]: [AttachmentInput[] | null, Function]
    = useState<StateValueDefault>(null);
  const [commentAttachmentFiles, setCommentAttachmentFiles]: [File[] | null, Function]
    = useState<StateValueDefault>(null);
  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');
  const [isMessageError, setIsMessageError]: [boolean, Function] = useState(false);
  const [commentEditId, setCommentEditId]: [number | null, Function] = useState(null);
  const [commentActiveId, setCommentActiveId]: [number | null, Function] = useState(null);
  const [commentLabel, setCommentLabel]: [string | null, Function] = useState(null);

  const contactCommentListViewPermissions: string[] = useMemo((): string[] => [CONTACT_COMMENT_LIST_VIEW], []);
  const contactCommentEditPermissions: string[] = useMemo((): string[] => [CONTACT_COMMENT_EDIT], []);
  const contactCommentDeletePermissions: string[] = useMemo((): string[] => [CONTACT_COMMENT_DELETE], []);
  const contactCommentCreatePermissions: string[] = useMemo((): string[] => [CONTACT_COMMENT_CREATE], []);

  const commentAttachmentDropDeps: DepsItem[] = useMemo((): DepsItem[] => [
    commentAttachmentFiles,
  ], [commentAttachmentFiles]);
  const commonAttachmentFiles: (AttachmentInput | File)[] = useMemo((): (AttachmentInput | File)[] => [
    ...(commentAttachments || []),
    ...(commentAttachmentFiles || []),
  ], [commentAttachments, commentAttachmentFiles]);

  const mentionList: MentionItem[] = useMemo((): MentionItem[] => (
    contactLinkedUserList && contactLinkedUserList.length
      ? contactLinkedUserList.map((contactItem: Contact): MentionItem => ({
        id: `${SIGN_AT}${contactItem.fullName}`,
        contactId: contactItem.id,
        fullName: contactItem.fullName,
        photo: contactItem.photo,
        photoThumbnail: contactItem.photoThumbnail,
      })) : []
  ), [contactLinkedUserList]);

  const hashtagList: HashtagItem[] = useMemo((): HashtagItem[] => (
    tagList && tagList.length ? tagList.map((tagItem: Tag): HashtagItem => ({
      id: `${SIGN_HASH}${tagItem.name}`,
      tagId: tagItem.id,
      name: tagItem.name,
    })) : []
  ), [tagList]);

  const onSaveCommentSuccess: Function = useCallback((commentId?: number): void => {
    const lastComment: Comment | null = commentList && commentList.length ? commentList[commentList.length - 1] : null;
    const resultCommentId: number | null = commentId || (lastComment && lastComment.id) || null;

    if (resultCommentId) {
      CommentService.scrollToComment(resultCommentId);
    }
  }, [commentList]);

  const saveComment: Function = useCallback((): void => {
    const mentionContactIds: number[] = commentText
      ? FormatService.extractHtmlAttributes(commentText, HTML_TAG_NAME_ANCHOR, DATA_CONTACT_ID, true) as number[] : [];

    if (commentEditId) {
      if (onUpdateComment && (commentText || (commentAttachments && commentAttachments.length)
        || (commentAttachmentFiles && commentAttachmentFiles.length))) {
        onUpdateComment(
          commentText, commentAttachments, commentAttachmentFiles, commentLabel,
          commentEditId, mentionContactIds, onSaveCommentSuccess,
        );
      }
      setCommentEditId(null);
    } else if (onAddComment && (commentText || (commentAttachmentFiles && commentAttachmentFiles.length))) {
      onAddComment(commentText, commentAttachmentFiles, commentLabel, mentionContactIds, onSaveCommentSuccess);
    }

    setCommentText(null);
    setCommentAttachmentFiles(null);
    setCommentAttachments(null);
    setCommentLabel(null);
  }, [commentText, commentAttachmentFiles, commentAttachments, commentEditId, commentLabel, commentList]);

  const onCommentFieldChange: Function = useCallback((event: React.ChangeEvent, commentTextValue: string): void => {
    setCommentText(commentTextValue);
  }, []);

  const onCommentFieldKeyDown: Function = useCallback((event: React.KeyboardEvent, commentTextValue: string): void => {
    if (event.key === KEYBOARD_KEY_ARROW_UP && !commentTextValue) {
      CommentService.editLastComment();

      event.preventDefault();
    }

    if (event.key === KEYBOARD_KEY_ESCAPE) {
      onCancelCommentForm();
    }

    if (event.key === KEYBOARD_KEY_ENTER && !event.shiftKey) {
      setCommentText(commentTextValue);

      CommentService.sendComment();
    }
  }, []);

  const onCommentAttachmentFilesChange: Function = useCallback((files: File[]): void => {
    setCommentAttachmentFiles([...(commentAttachmentFiles || []), ...files]);
  }, [commentAttachmentFiles]);

  const onCommentAttachmentFileDelete: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, fileIndex: number,
  ): void => {
    const isCommentEditing: boolean = !!commentEditId;
    const currentCommentAttachmentFiles: File[] = [...(commentAttachmentFiles || [])];
    const currentCommentAttachments: AttachmentInput[] = [...(commentAttachments || [])];

    if (fileIndex !== null && fileIndex >= 0) {
      if (isCommentEditing && fileIndex < currentCommentAttachments.length) {
        const newCommentAttachments: AttachmentInput[] = currentCommentAttachments;
        StructureService.deleteListItemByIndex(newCommentAttachments, fileIndex);
        setCommentAttachments(newCommentAttachments);
      } else {
        const newCommentAttachmentFiles: File[] = currentCommentAttachmentFiles;
        StructureService.deleteListItemByIndex(
          newCommentAttachmentFiles,
          fileIndex - (isCommentEditing ? currentCommentAttachments.length : 0),
        );
        setCommentAttachmentFiles(newCommentAttachmentFiles);
      }
    }
  }, [commentAttachmentFiles, commentAttachments]);

  const onCommentAttachmentFileError: Function = useCallback((error: string): void => {
    setStatusMessage(error);
    setIsOpenMessage(true);
    setIsMessageError(true);
  }, []);

  const closeMessage: Function = useCallback((): void => {
    setIsMessageError(false);
    setIsOpenMessage(false);
    setStatusMessage('');
  }, []);

  const switchCommentEditMode: Function = useCallback((event: React.SyntheticEvent, comment: Comment): void => {
    const isEditModeActive: boolean = !commentEditId || commentEditId !== comment.id;

    setCommentEditId(isEditModeActive ? comment.id : null);
    setCommentText(isEditModeActive ? (comment.text || '') : null);
    setCommentLabel(comment.label ? comment.label.name : null);

    const commentAttachmentItems: Attachment[] | null = comment.attachments && comment.attachments.length
      ? comment.attachments : null;
    setCommentAttachments(isEditModeActive && commentAttachmentItems
      ? commentAttachmentItems.map((attachmentItem: Attachment): AttachmentInput => ({
        id: attachmentItem.id,
        name: attachmentItem.file.name,
        link: attachmentItem.file.linkName,
      })) : null);
    setCommentAttachmentFiles(isEditModeActive ? [] : null);

    event.stopPropagation();
  }, [commentEditId, commentList]);

  const onDeleteCommentCancel: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, commentId: number,
  ): void => {
    if (commentActiveId === commentId) {
      setCommentActiveId(null);
    }

    event.stopPropagation();
  }, [commentActiveId]);

  const onCancelCommentForm: Function = useCallback((): void => {
    setCommentAttachmentFiles(null);
    setCommentAttachments(null);
    setCommentActiveId(null);
    setCommentEditId(null);
    setCommentText(null);
    setCommentLabel(null);
  }, []);

  const onDeleteCommentConfirm: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, commentId: number,
  ): void => {
    if (onDeleteComment) {
      onDeleteComment(commentId, onSaveCommentSuccess);
    }

    onCancelCommentForm();

    event.stopPropagation();
  }, [commentList]);

  const onCommentActiveIdChange: Function = useCallback((
    event: React.ChangeEvent, commentId: number,
  ): void => {
    setCommentActiveId(commentId);

    event.stopPropagation();
  }, []);

  const onCommentLabelChange: Function = useCallback((newCommentLabel: string): void => {
    setCommentLabel(newCommentLabel);
  }, []);

  return (
    <Widget
      isFullWidth
      isFullHeight
      widgetName={WIDGET_NAME}
      defaultWidgetTitle={WIDGET_TITLE_DEFAULT}
    >
      <CheckAccess permissions={contactCommentListViewPermissions}>
        <CommentTable
          queryCommentId={queryCommentId}
          commentList={commentList}
          commentEditId={commentEditId}
          commentActiveId={commentActiveId}
          currentUser={currentUser}
          editPermissions={contactCommentEditPermissions}
          deletePermissions={contactCommentDeletePermissions}
          mentionPermission={CONTACT_VIEW}
          hashtagPermission={SEARCH_RESULT_VIEW}
          labelIconMap={LABEL_ICON_MAP}
          onSwitchCommentEditMode={switchCommentEditMode}
          onDeleteCommentConfirm={onDeleteCommentConfirm}
          onDeleteCommentCancel={onDeleteCommentCancel}
          onCommentActiveIdChange={onCommentActiveIdChange}
          onCommentEntityLinkClick={onCommentEntityLinkClick}
        />
      </CheckAccess>
      <CheckAccess permissions={contactCommentCreatePermissions}>
        <CommentForm
          withLabels
          isCommentSaving={isCommentSaving}
          attachmentFiles={commonAttachmentFiles}
          commentText={commentText}
          commentLabel={commentLabel}
          mentionList={mentionList}
          hashtagList={hashtagList}
          commentAttachmentDropDeps={commentAttachmentDropDeps}
          mentionRouteRoot={ROUTE_CONTACT}
          hashtagRouteRoot={ROUTE_SEARCH}
          commentLabelList={commentLabelList}
          labelIconMap={LABEL_ICON_MAP}
          onSaveComment={saveComment}
          onCommentFieldChange={onCommentFieldChange}
          onCommentFieldKeyDown={onCommentFieldKeyDown}
          onCommentAttachmentFilesChange={onCommentAttachmentFilesChange}
          onCommentAttachmentFileDelete={onCommentAttachmentFileDelete}
          onCommentAttachmentFileError={onCommentAttachmentFileError}
          onCommentLabelChange={onCommentLabelChange}
        />
      </CheckAccess>
      <StatusBar isOpen={isOpenMessage} isError={isMessageError} message={statusMessage} onClose={closeMessage} />
    </Widget>
  );
});

export default ContactWidgetActivity;
